import React, { useState } from 'react';
import { Lock, Mail, LogIn } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { CHURCH_INFO } from '../../utils/constants';
import Button from '../common/Button';
import TwoFactorModal from './TwoFactorModal';

// Composant Login principal
const Login = ({ onSwitchToRegister, onSwitchToForgotPassword }) => {
  const { login } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // États pour 2FA
  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const [tempUserData, setTempUserData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    // Validation
    if (!formData.email || !formData.password) {
      setError('Veuillez remplir tous les champs');
      setIsLoading(false);
      return;
    }

    // Tentative de connexion
    const result = await login(formData.email, formData.password);
    
    if (!result.success) {
      // Si 2FA est requis
      if (result.requiresTwoFactor) {
        setTempUserData(result);
        setShowTwoFactorModal(true);
        setIsLoading(false);
        return;
      }
      
      // Autre erreur
      setError(result.error);
      setIsLoading(false);
      return;
    }

    // Connexion réussie
    setIsLoading(false);
    // La redirection est gérée automatiquement par AuthContext
  };

  const handleTwoFactorSuccess = () => {
    setShowTwoFactorModal(false);
    setTempUserData(null);
    // La redirection est gérée automatiquement par AuthContext
  };

  const handleTwoFactorCancel = () => {
    setShowTwoFactorModal(false);
    setTempUserData(null);
    setError('');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 max-w-md w-full">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center">
                <Lock size={32} className="text-white" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">
              {CHURCH_INFO.name}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-sm">{CHURCH_INFO.fullName.vomm}</p>
            <p className="text-gray-600 dark:text-gray-300 text-sm mb-2">{CHURCH_INFO.fullName.fjkm}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">{CHURCH_INFO.city}, {CHURCH_INFO.country}</p>
          </div>

          {/* Formulaire de connexion */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Adresse e-mail
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail size={20} className="text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="email"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="exemple@email.com"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Mot de passe
                </label>
                <button
                  type="button"
                  onClick={onSwitchToForgotPassword}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                  disabled={isLoading}
                >
                  Mot de passe oublié ?
                </button>
              </div>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock size={20} className="text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="current-password"
                  className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg pl-10 pr-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Message d'erreur */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-4 py-3 rounded-lg">
                <p className="text-sm">{error}</p>
              </div>
            )}

            {/* Bouton de connexion */}
            <Button
              type="submit"
              className="w-full"
              icon={LogIn}
              isLoading={isLoading}
              disabled={isLoading}
            >
              {isLoading ? 'Connexion...' : 'Se connecter'}
            </Button>
          </form>

          {/* Lien vers inscription */}
          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Pas encore de compte ?{' '}
              <button
                type="button"
                onClick={onSwitchToRegister}
                className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                disabled={isLoading}
              >
                S'inscrire
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Modal 2FA */}
      {showTwoFactorModal && tempUserData && (
        <TwoFactorModal
          email={formData.email}
          userId={tempUserData.userId}
          twoFactorMethod={tempUserData.twoFactorMethod}
          onSuccess={handleTwoFactorSuccess}
          onCancel={handleTwoFactorCancel}
        />
      )}
    </>
  );
};

export default Login;